import React, { useState, useCallback, useEffect } from 'react';
import {
  Layout,
  Card,
  Button,
  FormLayout,
  TextField,
  Select,
  RangeSlider,
  DataTable,
  LegacyStack,
  TextStyle,
  Banner,
  Text,
  Spinner,
  Icon,
  Badge,
  Modal,
  Tooltip,
  RadioButton,
  ChoiceList
} from '@shopify/polaris';
import { 
  CircleTickMajor, 
  CircleAlertMajor, 
  CircleDisabledMajor,
  CircleInformationMajor,
  SaveMinor
} from '@shopify/polaris-icons';
import { getAvailableAIModels } from '../../services/aiModelService';
import { compareSizecharts } from '../../services/sizechartAIService';
import { updateSizechart } from '../../services/sizechartService';
import { saveUserPreference, getUserPreferences } from '../../services/userPreferenceService';

export default function SizechartChecker({ shopId, userId, groups, sizecharts, settings, refreshData }) {
  // State for sizechart selection
  const [selectedSizechartId, setSelectedSizechartId] = useState('');
  const [selectedSizechart, setSelectedSizechart] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  
  // State for product sizechart input
  const [inputType, setInputType] = useState('text'); // 'text' or 'image'
  const [productSizechartText, setProductSizechartText] = useState('');
  const [productSizechartImageUrl, setProductSizechartImageUrl] = useState('');
  const [parsedProductSizechart, setParsedProductSizechart] = useState(null);
  const [parsingError, setParsingError] = useState('');
  const [isParsingImage, setIsParsingImage] = useState(false);
  
  // State for country selection
  const [selectedCountries, setSelectedCountries] = useState(settings?.countries || ['US']);
  
  // Use the allowed deviation directly from settings
  const allowedDeviation = settings?.allowedDeviation || 3;
  
  // State for saved user preferences
  const [savedSettings, setSavedSettings] = useState(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  
  // State for AI model selection
  const [aiModels, setAiModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [creditCost, setCreditCost] = useState(1);
  
  // State for comparison results
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [comparisonError, setComparisonError] = useState('');
  
  // State for result modal
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  
  // Load AI models and saved settings on component mount
  useEffect(() => {
    loadAIModels();
    loadSavedSettings();
  }, []);
  
  // Update selected sizechart when ID changes
  useEffect(() => {
    if (selectedSizechartId && sizecharts.length > 0) {
      const chart = sizecharts.find(c => c.id === selectedSizechartId);
      setSelectedSizechart(chart || null);
      
      // Set default countries from the selected sizechart
      if (chart && chart.countries) {
        setSelectedCountries(chart.countries);
      }
    } else {
      setSelectedSizechart(null);
    }
  }, [selectedSizechartId, sizecharts]);
  
  // Set initial sizechart selection when sizecharts are loaded
  useEffect(() => {
    if (sizecharts.length > 0) {
      // Default to the first sizechart
      setSelectedSizechartId(sizecharts[0].id);
    } else {
      setSelectedSizechartId('');
    }
  }, [sizecharts]);
  
  // Update selected model when ID changes
  useEffect(() => {
    if (selectedModelId && aiModels.length > 0) {
      const model = aiModels.find(m => m.id === selectedModelId);
      setSelectedModel(model || null);
      setCreditCost(model?.creditCostPerOutput || 1);
    } else if (aiModels.length > 0) {
      // Default to the cheapest model
      const cheapestModel = [...aiModels]
        .filter(m => m.isEnabled)
        .sort((a, b) => (a.creditCostPerOutput || 1) - (b.creditCostPerOutput || 1))[0];
      
      if (cheapestModel) {
        setSelectedModelId(cheapestModel.id);
        setSelectedModel(cheapestModel);
        setCreditCost(cheapestModel.creditCostPerOutput || 1);
      }
    }
  }, [selectedModelId, aiModels]);
  
  // Load AI models from the server
  const loadAIModels = async () => {
    try {
      const models = await getAvailableAIModels();
      // Filter out disabled models
      const enabledModels = models.filter(model => model.isEnabled);
      setAiModels(enabledModels);
      console.log('Loaded AI models:', enabledModels.map(m => ({ id: m.id, name: m.displayName, type: m.type })));
    } catch (error) {
      console.error('Error loading AI models:', error);
    }
  };
  
  // Apply saved model settings after models are loaded
  useEffect(() => {
    // Only proceed if we have models loaded and saved settings
    if (aiModels.length > 0 && savedSettings?.selectedModelId) {
      console.log('Attempting to apply saved model ID:', savedSettings.selectedModelId, 
                'type:', savedSettings.selectedModelType || 'unknown', 
                'for input type:', inputType);
      
      // First try to find an exact match by both modelId and type
      let modelToApply = null;
      
      if (savedSettings.selectedModelType) {
        // Try to find exact match with both modelId and type
        modelToApply = aiModels.find(m => 
          m.modelId === savedSettings.selectedModelId && 
          (m.type || 'text') === savedSettings.selectedModelType);
        
        if (modelToApply) {
          console.log('Found exact model match by ID and type:', modelToApply.displayName);
        }
      }
      
      // If no exact match or no type was saved, find models just by modelId
      if (!modelToApply) {
        const matchingModels = aiModels.filter(m => m.modelId === savedSettings.selectedModelId);
        console.log('Found matching models by ID:', matchingModels.map(m => ({ id: m.id, name: m.displayName, type: m.type || 'text' })));
        
        if (matchingModels.length > 0) {
          // Find a model that matches the current input type
          const compatibleModel = matchingModels.find(model => {
            if (inputType === 'text') {
              return !model.type || model.type === 'text';
            } else { // image
              return model.type === 'vision';
            }
          });
          
          if (compatibleModel) {
            console.log('Found compatible model for current input type:', compatibleModel.displayName);
            modelToApply = compatibleModel;
          } else {
            console.log('No compatible model found for current input type. Models found:', 
              matchingModels.map(m => `${m.displayName} (${m.type || 'text'})`).join(', '));
          }
        } else {
          console.log('No models found with modelId:', savedSettings.selectedModelId);
          console.log('Available models:', aiModels.map(m => ({ id: m.id, modelId: m.modelId, name: m.displayName, type: m.type || 'text' })));
        }
      }
      
      // Apply the model if we found one
      if (modelToApply) {
        console.log('Applying saved model:', modelToApply.displayName, 
                  'with type:', modelToApply.type || 'text', 
                  'document ID:', modelToApply.id);
        // Set all model properties directly to prevent race conditions
        setSelectedModelId(modelToApply.id); // Set the document ID for internal use
        setSelectedModel(modelToApply); // Set the model object directly
        setCreditCost(modelToApply.creditCostPerOutput || 1); // Set credit cost directly
        
        // Set a flag to prevent other effects from overriding this
        window.appliedSavedModel = true;
        console.log('Applied saved model directly, skipping other effects');
        
        // Clear the flag after a short delay to allow for future changes
        setTimeout(() => {
          window.appliedSavedModel = false;
        }, 1000);
      }
    }
  }, [aiModels, savedSettings, inputType]);
  
  // Update model selection when input type changes
  useEffect(() => {
    if (aiModels.length === 0) return;
    
    // Skip if we just applied a saved model (prevents race conditions)
    if (window.appliedSavedModel) {
      console.log('Skipping input type effect because we just applied a saved model');
      return;
    }
    
    // Skip this effect if we're loading a saved model
    // This prevents the saved model from being overridden by the default selection
    if (savedSettings?.selectedModelId && !selectedModel) {
      console.log('Skipping input type effect because we\'re loading a saved model');
      return;
    }
    
    // Filter models based on input type
    const compatibleModels = aiModels.filter(model => {
      if (inputType === 'text') {
        // If no type is specified, assume it's a text model
        return !model.type || model.type === 'text';
      } else { // image
        return model.type === 'vision';
      }
    });
    
    if (compatibleModels.length === 0) {
      // No compatible models, clear selection
      setSelectedModelId('');
      setSelectedModel(null);
      setCreditCost(0);
      return;
    }
    
    // Check if current model is compatible
    const currentModelCompatible = selectedModel && compatibleModels.some(m => m.id === selectedModel.id);
    
    if (!currentModelCompatible) {
      console.log('Current model is not compatible with input type, switching to default');
      // Set default model (cheapest compatible model)
      const cheapestModel = [...compatibleModels]
        .sort((a, b) => (a.creditCostPerOutput || 1) - (b.creditCostPerOutput || 1))[0];
      
      setSelectedModelId(cheapestModel.id);
      setSelectedModel(cheapestModel);
      setCreditCost(cheapestModel.creditCostPerOutput || 1);
    }
  }, [inputType, aiModels, selectedModel, savedSettings]);
  
  // Force UI update when selectedModelId changes
  useEffect(() => {
    // This is a no-op effect that ensures the UI re-renders when selectedModelId changes
    // This helps ensure the dropdown shows the correct selected value
    console.log('Selected model ID changed to:', selectedModelId);
    
    // Verify if this ID exists in the current model options
    const modelExists = modelOptions?.some(option => option.value === selectedModelId);
    console.log('Model exists in dropdown options:', modelExists);
    
    if (selectedModelId && !modelExists) {
      console.warn('Selected model ID is not in the current options list');
    }
  }, [selectedModelId]);
  
  // Handle parsing product sizechart (text or image)
  const handleParseProductSizechart = async () => {
    try {
      if (!selectedSizechart) {
        throw new Error('Please select a master sizechart first');
      }
      
      if (inputType === 'text') {
        // Generate a structured prompt for the AI
        const prompt = generateAIPrompt(
          selectedSizechart,
          'text',
          productSizechartText,
          selectedCountries,
          allowedDeviation
        );
        
        console.log('Sending AI prompt for text processing:', prompt);
        
        try {
          // In a real implementation, this would call the AI service with the prompt
          const result = await compareSizecharts({
            shopId,
            userId,
            masterSizechartId: selectedSizechart.id,
            productSizechartData: {
              type: 'text',
              content: productSizechartText
            },
            modelId: selectedModelId,  // Use the selected model ID
            countries: selectedCountries,
            prompt: prompt // Add the structured prompt
          });
          
          // If we have a real result, use it
          if (result && result.extractedData) {
            setParsedProductSizechart(result.extractedData);
          } else {
            // For development/testing, fall back to local parsing
            const parsed = parseProductSizechartText(productSizechartText, selectedSizechart);
            setParsedProductSizechart(parsed);
          }
          setParsingError('');
        } catch (textError) {
          console.error('Error processing text with AI:', textError);
          // Fall back to local parsing if AI fails
          try {
            const parsed = parseProductSizechartText(productSizechartText, selectedSizechart);
            setParsedProductSizechart(parsed);
            setParsingError('');
          } catch (fallbackError) {
            throw new Error(`Failed to process text: ${fallbackError.message || 'Unknown error'}`);
          }
        }
      } else {
        // Process image URL
        setIsParsingImage(true);
        setParsingError('');
        
        // Check if we have a valid model for image processing
        const visionModel = aiModels.find(model => model.type === 'vision');
        if (!visionModel) {
          throw new Error('No vision-capable AI model available. Please check your AI model settings.');
        }
        
        // Call the AI service to extract data from the image
        try {
          // This would call a backend service that uses the vision model to extract the sizechart data
          // For now, we'll simulate this with a placeholder
          const extractedData = await extractDataFromImage(productSizechartImageUrl, selectedSizechart, visionModel.id);
          setParsedProductSizechart(extractedData);
        } catch (imageError) {
          console.error('Error extracting data from image:', imageError);
          throw new Error(`Failed to extract data from image: ${imageError.message || 'Unknown error'}`);
        } finally {
          setIsParsingImage(false);
        }
      }
    } catch (error) {
      console.error('Error processing sizechart input:', error);
      setParsingError(error.message || 'Failed to process sizechart input');
      setParsedProductSizechart(null);
      setIsParsingImage(false);
    }
  };
  
  // Generate a structured prompt for the AI model
  const generateAIPrompt = (masterSizechart, inputType, inputData, targetMarkets, allowedDeviation) => {
    // Get the clothing type name
    const clothingType = groups.find(g => g.id === masterSizechart.groupId)?.name || 'Unknown';
    
    // Format the master sizechart data for the prompt
    const masterSizechartFormatted = {
      name: masterSizechart.name,
      clothingType,
      columns: masterSizechart.columns.map(col => ({
        name: col.name,
        unit: col.unit
      })),
      rows: masterSizechart.rows.map(row => ({
        size: row.size,
        values: row.values
      }))
    };
    
    // Create a structured prompt
    const prompt = {
      task: 'sizechart_comparison',
      masterSizechart: masterSizechartFormatted,
      inputType,
      inputData,
      targetMarkets,
      allowedDeviation,
      instructions: `
        You are a specialized AI for comparing clothing sizecharts. 
        Your task is to analyze the provided product sizechart data (${inputType}) and compare it to the master sizechart.
        
        The master sizechart is for: ${clothingType}
        Target markets for comparison: ${targetMarkets.join(', ')}
        Allowed deviation: ${allowedDeviation}cm
        
        Please extract the size information from the provided ${inputType} data and format it to match the master sizechart structure.
        Then compare each measurement to the master sizechart and determine if the differences are within the allowed deviation (${allowedDeviation}cm).
        
        Return the extracted data in a structured format matching the master sizechart, and provide an analysis of the differences.
        Classify the overall match as one of: 'match' (all within deviation), 'minor_differences' (some exceed deviation but by small amount), or 'major_differences' (significant deviations).
      `
    };
    
    return prompt;
  };
  
  // Extract data from sizechart image using AI vision model
  const extractDataFromImage = async (imageUrl, masterSizechart, modelId) => {
    // This would be an API call to the backend service that uses AI vision to extract the sizechart data
    // For now, we'll simulate this with a placeholder implementation
    
    try {
      // Generate a structured prompt for the AI
      const prompt = generateAIPrompt(
        masterSizechart,
        'image',
        imageUrl,
        selectedCountries,
        allowedDeviation
      );
      
      console.log('Sending AI prompt for image processing:', prompt);
      
      // In a real implementation, this would call the sizechartAIService with the prompt
      const result = await compareSizecharts({
        shopId,
        userId,
        masterSizechartId: masterSizechart.id,
        productSizechartData: {
          type: 'image',
          url: imageUrl
        },
        modelId,
        countries: selectedCountries,
        prompt: prompt // Add the structured prompt
      });
      
      // The service would return the extracted data in the same format as parseProductSizechartText
      // For now, we'll create a simple structure matching the master sizechart
      
      // If we have a real result, use it
      if (result && result.extractedData) {
        return result.extractedData;
      }
      
      // Otherwise use a placeholder (this would be removed in production)
      const columns = [...masterSizechart.columns];
      const rows = masterSizechart.rows.map(row => ({
        size: row.size,
        values: row.values.map((v, i) => {
          // Simulate slight variations from the master chart
          const val = parseFloat(v);
          if (isNaN(val)) return v;
          return (val + (Math.random() * 2 - 1)).toFixed(1);
        })
      }));
      
      return { columns, rows };
    } catch (error) {
      console.error('Error in extractDataFromImage:', error);
      throw new Error(`Failed to extract data from image: ${error.message}`);
    }
  };
  
  // Parse product sizechart text into a structured format
  const parseProductSizechartText = (text, masterSizechart) => {
    // Split the text into lines
    const lines = text.trim().split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      throw new Error('No data found in the product sizechart text');
    }
    
    // Try to determine if the first line contains headers
    const firstLine = lines[0].trim();
    // Improved regex to detect sizes including ranges like 34-38
    const hasHeaders = !firstLine.match(/^(XS|S|M|L|XL|XXL|\d+(-\d+)?)/i); // Assume headers if first line doesn't start with a size or size range
    
    let headers = [];
    let rows = [];
    
    if (hasHeaders) {
      // Extract headers from the first line
      headers = firstLine.split(/\s+|\t+|,/).map(h => h.trim()).filter(h => h);
      
      // Process data rows
      for (let i = 1; i < lines.length; i++) {
        const rowData = lines[i].split(/\s+|\t+|,/).map(d => d.trim()).filter(d => d);
        if (rowData.length > 0) {
          const size = rowData[0];
          // Keep values as strings to support ranges like '34-38'
          const values = rowData.slice(1).map(value => {
            // Ensure the value is a string (could be a number or range)
            return value.toString().trim();
          });
          rows.push({ size, values });
        }
      }
    } else {
      // No headers, use master sizechart headers
      headers = masterSizechart.columns.map(col => col.name);
      
      // Process all lines as data rows
      for (let i = 0; i < lines.length; i++) {
        const rowData = lines[i].split(/\s+|\t+|,/).map(d => d.trim()).filter(d => d);
        if (rowData.length > 0) {
          const size = rowData[0];
          // Keep values as strings to support ranges like '34-38'
          const values = rowData.slice(1).map(value => {
            // Ensure the value is a string (could be a number or range)
            return value.toString().trim();
          });
          rows.push({ size, values });
        }
      }
    }
    
    // Validate the parsed data
    if (headers.length < 2) {
      throw new Error('Could not identify enough column headers in the product sizechart');
    }
    
    if (rows.length === 0) {
      throw new Error('No size data found in the product sizechart');
    }
    
    // Create columns array matching the master sizechart format
    const columns = headers.map((header, index) => {
      if (index === 0) {
        return { name: 'Size', unit: 'none' };
      } else {
        // Try to match with master sizechart columns
        const matchingColumn = masterSizechart.columns.find(col => 
          col.name.toLowerCase() === header.toLowerCase() ||
          col.name.toLowerCase().includes(header.toLowerCase()) ||
          header.toLowerCase().includes(col.name.toLowerCase())
        );
        
        return matchingColumn || { name: header, unit: 'cm' }; // Default to cm if no match
      }
    });
    
    return {
      columns,
      rows
    };
  };
  
  // Handle direct comparison with AI processing
  const handleCompare = async () => {
    try {
      if (!selectedSizechart) {
        setComparisonError('Please select a master sizechart template');
        return;
      }
      
      // Validate input based on selected input type
      if (inputType === 'text' && !productSizechartText.trim()) {
        setComparisonError('Please enter product sizechart data');
        return;
      }
      
      if (inputType === 'image' && !productSizechartImageUrl.trim()) {
        setComparisonError('Please enter a sizechart image URL');
        return;
      }
      
      // Validate AI model selection
      if (!selectedModelId) {
        setComparisonError('Please select an AI model');
        return;
      }
      
      setIsComparing(true);
      setComparisonError('');
      setComparisonResult(null);
      setParsedProductSizechart(null);
      
      // Get clothing type information
      const clothingType = selectedGroupId ? 
        groups.find(g => g.id === selectedGroupId)?.name : 
        groups.find(g => g.id === selectedSizechart.groupId)?.name;
      
      // Prepare the request data with direct input
      const requestData = {
        shopId,
        userId,
        masterSizechartId: selectedSizechart.id,
        productSizechartData: inputType === 'text' ? 
          { type: 'text', content: productSizechartText } : 
          { type: 'image', url: productSizechartImageUrl },
        modelId: selectedModelId,
        countries: selectedCountries,
        allowedDeviation,
        clothingType,
        // System-defined prompt (not exposed to user)
        systemPrompt: `Extract and compare the sizechart data for ${clothingType || 'clothing'} items. 
          The master sizechart is for ${selectedSizechart.name}. 
          Target markets: ${selectedCountries.join(', ')}. 
          Maximum allowed deviation: ${allowedDeviation}cm.`
      };
      
      // Call the AI service to process and compare sizecharts
      const result = await compareSizecharts(requestData);
      
      // If we have extracted data, set it for display
      if (result && result.extractedData) {
        setParsedProductSizechart(result.extractedData);
      }
      
      setComparisonResult(result);
      setIsResultModalOpen(true);
    } catch (error) {
      console.error('Error comparing sizecharts:', error);
      setComparisonError(error.message || 'Failed to compare sizecharts');
    } finally {
      setIsComparing(false);
    }
  };
  
  // Handle updating master sizechart with product data
  const handleUpdateMasterSizechart = async () => {
    try {
      if (!selectedSizechart || !parsedProductSizechart || !comparisonResult) {
        return;
      }
      
      // Confirm before updating
      if (!window.confirm('Are you sure you want to update the master sizechart with this product data?')) {
        return;
      }
      
      // Merge the parsed product sizechart with the master sizechart
      const updatedRows = [...selectedSizechart.rows];
      
      // Update existing sizes
      parsedProductSizechart.rows.forEach(productRow => {
        const existingRowIndex = updatedRows.findIndex(row => 
          row.size.toLowerCase() === productRow.size.toLowerCase()
        );
        
        if (existingRowIndex >= 0) {
          // Update existing row values
          const updatedValues = [...updatedRows[existingRowIndex].values];
          
          productRow.values.forEach((value, index) => {
            if (index < updatedValues.length) {
              updatedValues[index] = value;
            }
          });
          
          updatedRows[existingRowIndex] = {
            ...updatedRows[existingRowIndex],
            values: updatedValues
          };
        } else {
          // Add new size
          updatedRows.push(productRow);
        }
      });
      
      // Update the master sizechart
      await updateSizechart(shopId, selectedSizechart.id, {
        rows: updatedRows
      });
      
      // Refresh data and close modal
      refreshData();
      setIsResultModalOpen(false);
      setComparisonResult(null);
      setParsedProductSizechart(null);
      setProductSizechartText('');
      
      alert('Master sizechart updated successfully!');
    } catch (error) {
      console.error('Error updating master sizechart:', error);
      alert('Failed to update master sizechart: ' + (error.message || 'Unknown error'));
    }
  };
  
  // Load saved user settings from Firebase
  const loadSavedSettings = async () => {
    try {
      console.log(`Loading saved settings for shop ${shopId}, user ${userId}`);
      const preferences = await getUserPreferences(shopId, userId, 'sizechartChecker');
      
      if (preferences) {
        console.log('Loaded saved preferences:', preferences);
        setSavedSettings(preferences);
        
        // Apply non-model settings immediately
        if (preferences.inputType) {
          console.log(`Setting input type to ${preferences.inputType}`);
          setInputType(preferences.inputType);
        }
        
        if (preferences.selectedCountries) {
          console.log(`Setting selected countries to ${preferences.selectedCountries.join(', ')}`);
          setSelectedCountries(preferences.selectedCountries);
        }
        
        if (preferences.selectedModelId) {
          console.log(`Saved model ID found: ${preferences.selectedModelId}`);
          console.log('Model settings will be applied in useEffect after models are loaded');
        }
      } else {
        console.log('No saved preferences found');
      }
    } catch (error) {
      console.error('Error loading saved settings:', error);
    }
  };
  
  // Save current settings to Firebase
  const saveCurrentSettings = async () => {
    try {
      setIsSavingSettings(true);
      
      // Ensure we have a valid model ID
      if (!selectedModelId && aiModels.length > 0) {
        console.warn('No model selected, using default model');
        // Use the current model if available, otherwise use the first compatible model
        const compatibleModels = aiModels.filter(model => {
          if (inputType === 'text') {
            return !model.type || model.type === 'text';
          } else { // image
            return model.type === 'vision';
          }
        });
        
        if (compatibleModels.length > 0) {
          const defaultModel = compatibleModels[0];
          console.log('Using default model for saving:', defaultModel.displayName);
          setSelectedModelId(defaultModel.id);
        }
      }
      
      // Get the current selected model
      const currentModel = selectedModel || aiModels.find(m => m.id === selectedModelId);
      
      // Log current state before saving
      console.log('Current state before saving:', {
        shopId,
        userId,
        inputType,
        selectedCountries,
        selectedModelId,
        selectedModel: currentModel ? currentModel.displayName : null,
        modelId: currentModel ? currentModel.modelId : null  // The actual provider model ID
      });
      
      // Collect all user preferences to save - use modelId and model type
      const settingsToSave = {
        inputType,
        selectedCountries,
        selectedModelId: currentModel ? currentModel.modelId : null,  // Save the modelId field, not the document ID
        selectedModelType: currentModel ? (currentModel.type || 'text') : null,  // Also save the model type
        lastUpdated: new Date().toISOString()
      };
      
      console.log('Saving settings to Firebase:', settingsToSave);
      
      // Save to Firebase
      await saveUserPreference(shopId, userId, 'sizechartChecker', settingsToSave);
      setSavedSettings(settingsToSave);
      
      // Show success message
      alert('Your preferences have been saved and will be applied automatically next time you use the Product Size Check.');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('There was an error saving your preferences. Please try again.');
    } finally {
      setIsSavingSettings(false);
    }
  };
  
  // Handle country selection
  const handleCountryChange = (selected) => {
    setSelectedCountries(selected);
  };
  
  // Prepare group options for select
  const groupOptions = groups.map(group => ({
    label: group.name,
    value: group.id
  }));
  
  // Prepare sizechart options for select with clothing type information
  const sizechartOptions = sizecharts.map(chart => {
    const groupName = groups.find(g => g.id === chart.groupId)?.name || 'Unknown';
    return {
      label: `${chart.name} (${groupName})`,
      value: chart.id
    };
  });
    
  // Prepare AI model options for select with clean names, filtered by input type
  const modelOptions = aiModels
    .filter(model => {
      if (inputType === 'text') {
        // If no type is specified, assume it's a text model
        return !model.type || model.type === 'text';
      } else { // image
        return model.type === 'vision';
      }
    })
    .map(model => ({
      label: model.type === 'vision' ? `${model.displayName} (Vi)` : model.displayName,
      value: model.id
    }));
    
  // Debug the selected model and available options
  console.log('Current selectedModelId:', selectedModelId);
  console.log('Available model options:', modelOptions.map(opt => ({ label: opt.label, value: opt.value })));
  console.log('Selected model details:', selectedModel ? {
    id: selectedModel.id,
    name: selectedModel.displayName,
    type: selectedModel.type || 'text',
    modelId: selectedModel.modelId
  } : 'None');
  
  // Country options for select
  const countryOptions = settings?.customCountries ? 
    [
      { label: 'United States (US)', value: 'US' },
      { label: 'United Kingdom (UK)', value: 'UK' },
      { label: 'European Union (EU)', value: 'EU' },
      { label: 'Australia (AU)', value: 'AU' },
      { label: 'New Zealand (NZ)', value: 'NZ' },
      { label: 'Canada (CA)', value: 'CA' },
      { label: 'Japan (JP)', value: 'JP' },
      { label: 'China (CN)', value: 'CN' },
      { label: 'South Korea (KR)', value: 'KR' },
      { label: 'Russia (RU)', value: 'RU' },
      { label: 'Brazil (BR)', value: 'BR' },
      { label: 'Mexico (MX)', value: 'MX' },
      { label: 'India (IN)', value: 'IN' },
      ...(settings.customCountries.map(c => ({
        label: `${c.name} (${c.code})`,
        value: c.code
      })))
    ].filter(country => settings.countries.includes(country.value)) : 
    [
      { label: 'United States (US)', value: 'US' },
      { label: 'United Kingdom (UK)', value: 'UK' },
      { label: 'European Union (EU)', value: 'EU' },
      { label: 'Australia (AU)', value: 'AU' },
      { label: 'New Zealand (NZ)', value: 'NZ' }
    ].filter(country => settings.countries?.includes(country.value) || true);
  
  // Render the comparison result status
  const renderResultStatus = (status) => {
    switch (status) {
      case 'match':
        return (
          <LegacyStack alignment="center" spacing="tight">
            <Icon source={CircleTickMajor} color="success" />
            <Text variant="headingMd" color="success">Perfect Match</Text>
          </LegacyStack>
        );
      case 'minor_differences':
        return (
          <LegacyStack alignment="center" spacing="tight">
            <Icon source={CircleAlertMajor} color="warning" />
            <Text variant="headingMd" color="warning">Minor Differences</Text>
          </LegacyStack>
        );
      case 'major_differences':
        return (
          <LegacyStack alignment="center" spacing="tight">
            <Icon source={CircleDisabledMajor} color="critical" />
            <Text variant="headingMd" color="critical">Major Differences</Text>
          </LegacyStack>
        );
      default:
        return (
          <LegacyStack alignment="center" spacing="tight">
            <Icon source={CircleInformationMajor} color="subdued" />
            <Text variant="headingMd" color="subdued">Unknown Status</Text>
          </LegacyStack>
        );
    }
  };
  
  // Prepare data for product sizechart preview table
  const getPreviewHeaders = (data) => {
    if (!data || !data.columns) return [];
    return data.columns.map(col => {
      return col.name;
    });
  };
  
  const getPreviewRows = (data) => {
    if (!data || !data.rows) return [];
    return data.rows.map(row => {
      const rowData = [row.size];
      for (let i = 0; i < (row.values || []).length; i++) {
        rowData.push(row.values[i]);
      }
      // Fill any missing values
      while (rowData.length < (data.columns?.length || 0)) {
        rowData.push('');
      }
      return rowData;
    });
  };
  
  // Prepare data for deviations table
  const getDeviationRows = () => {
    if (!comparisonResult || !comparisonResult.deviations) return [];
    
    return comparisonResult.deviations.map(deviation => [
      deviation.size,
      deviation.measurement,
      deviation.masterValue,
      deviation.productValue,
      deviation.difference,
      deviation.withinTolerance ? 
        <Badge status="success">Within Tolerance</Badge> : 
        <Badge status="critical">Exceeds Tolerance</Badge>
    ]);
  };
  
  return (
    <div style={{ height: 'auto', overflow: 'visible' }}>
      <div style={{ marginBottom: '24px' }}>
        <Card title="Select Master Sizechart" sectioned={false} padding="0">
          <div style={{ height: 'auto', overflow: 'visible' }}>
            <div style={{ padding: '16px' }}>
              <FormLayout>
                <Select
                  label="Sizechart Template"
                  options={sizechartOptions}
                  value={selectedSizechartId}
                  onChange={setSelectedSizechartId}
                  disabled={sizecharts.length === 0}
                  error={sizecharts.length === 0 ? 'No sizecharts available' : undefined}
                  helpText="Select the master sizechart to compare against"
                />
                
                {selectedSizechart && (
                  <Banner status="info" title="Selected Sizechart Information">
                    <p><strong>Name:</strong> {selectedSizechart.name}</p>
                    <p><strong>Clothing Type:</strong> {groups.find(g => g.id === selectedSizechart.groupId)?.name || '-'}</p>
                    <p><strong>Countries:</strong> {(selectedSizechart.countries || ['US']).join(', ')}</p>
                    <p><strong>Measurements:</strong> {selectedSizechart.columns.slice(1).map(col => col.name).join(', ')}</p>
                    <p><strong>Sizes:</strong> {selectedSizechart.rows.map(row => row.size).join(', ')}</p>
                  </Banner>
                )}
              </FormLayout>
            </div>
          </div>
        </Card>
      </div>
      
      <div style={{ marginBottom: '24px' }}>
        <Card padding="0">
          <div style={{ height: 'auto', overflow: 'visible' }}>
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ddd' }}>
              <Text variant="headingMd">Product Sizechart Data</Text>
              <Button
                icon={<Icon source={SaveMinor} />}
                onClick={saveCurrentSettings}
                loading={isSavingSettings}
                disabled={isSavingSettings}
                tooltip="Save these settings as default"
              />
            </div>
            <div style={{ padding: '16px' }}>
              <Text variant="headingMd" as="h3">AI Model & Input Type</Text>
              <div style={{ marginTop: '12px' }}>
                <FormLayout>
                  <div>
                    <Select
                      label="AI Model"
                      options={modelOptions}
                      value={selectedModelId}
                      onChange={setSelectedModelId}
                      disabled={aiModels.length === 0}
                      error={aiModels.length === 0 ? 'No AI models available' : undefined}
                    />
                    <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge status="info">
                        {creditCost} {creditCost === 1 ? 'credit' : 'credits'}
                      </Badge>
                      <Text variant="bodySm" color="subdued">
                        {inputType === 'text' ? 'Text models' : 'Vision models'} are automatically filtered
                      </Text>
                    </div>
                  </div>
                  
                  <ChoiceList
                    title="Select Input Type"
                    choices={[
                      {label: 'Text Input (paste structured data)', value: 'text'},
                      {label: 'Image Input (URL to sizechart image)', value: 'image'}
                    ]}
                    selected={[inputType]}
                    onChange={value => setInputType(value[0])}
                  />
                </FormLayout>
              </div>
            </div>
            
            <div style={{ padding: '16px' }}>
              <FormLayout>
                {inputType === 'text' ? (
                  <TextField
                    label="Product Sizechart Data"
                    value={productSizechartText}
                    onChange={setProductSizechartText}
                    multiline={8}
                    placeholder="Paste any sizechart data here - it doesn't need to be formatted in any specific way"
                    helpText="The AI model will extract the relevant size information from your input"
                    error={parsingError}
                  />
                ) : (
                  <TextField
                    label="Sizechart Image URL"
                    value={productSizechartImageUrl}
                    onChange={setProductSizechartImageUrl}
                    placeholder="https://example.com/sizechart.jpg"
                    helpText="Enter the URL of a sizechart image from a product page"
                    error={parsingError}
                  />
                )}
                
                {parsingError && (
                  <Banner status="critical">
                    <p>{parsingError}</p>
                  </Banner>
                )}
              </FormLayout>
            </div>
            
            {parsedProductSizechart && (
              <div style={{ padding: '16px' }}>
                <Text variant="headingMd" as="h3">Product Sizechart Preview</Text>
                <div style={{ marginTop: '12px' }}>
                  <DataTable
                    columnContentTypes={Array(parsedProductSizechart.columns.length).fill('text')}
                    headings={getPreviewHeaders(parsedProductSizechart)}
                    rows={getPreviewRows(parsedProductSizechart)}
                    truncate={false}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      
      <div style={{ marginBottom: '24px' }}>
        <Card padding="0">
          <div style={{ height: 'auto', overflow: 'visible' }}>
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ddd' }}>
              <Text variant="headingMd">Comparison Settings</Text>
            </div>
            <div style={{ padding: '16px' }}>
              <FormLayout>
                <Select
                  label="Product Clothing Type"
                  options={[{ label: 'Use selected sizechart type', value: '' }, ...groupOptions]}
                  value={selectedGroupId || ''}
                  onChange={setSelectedGroupId}
                  helpText="Optionally specify a different clothing type for this product"
                />
                
                <ChoiceList
                  allowMultiple
                  title="Target Markets for This Check"
                  choices={countryOptions}
                  selected={selectedCountries}
                  onChange={handleCountryChange}
                  helpText="Select which of your supported markets you want to check this product against"
                />
                
                <Banner status="info">
                  <p>You can manage your shop's supported countries in the Settings tab.</p>
                </Banner>
                
                <LegacyStack vertical spacing="tight">
                  <Text variant="bodyMd">Allowed Deviation: <strong>{allowedDeviation} cm</strong></Text>
                  <Text variant="bodySm" color="subdued">
                    Maximum allowed difference between master and product measurements.
                    You can change this in the Settings tab.
                  </Text>
                </LegacyStack>
                
                <div style={{ marginTop: '1rem' }}>
                  <Button 
                    primary 
                    size="large"
                    fullWidth
                    onClick={handleCompare} 
                    loading={isComparing}
                    disabled={!selectedSizechart || (inputType === 'text' && !productSizechartText.trim()) || (inputType === 'image' && !productSizechartImageUrl.trim()) || isComparing}
                  >
                    Check Product Sizechart
                  </Button>
                  {comparisonError && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <Banner status="critical">
                        <p>{comparisonError}</p>
                      </Banner>
                    </div>
                  )}
                </div>
              </FormLayout>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Result Modal */}
      <Modal
        open={isResultModalOpen}
        onClose={() => setIsResultModalOpen(false)}
        title="Sizechart Comparison Results"
        primaryAction={{
          content: 'Close',
          onAction: () => setIsResultModalOpen(false)
        }}
        secondaryActions={[
          {
            content: 'Update Master Sizechart',
            onAction: handleUpdateMasterSizechart,
            disabled: !comparisonResult || comparisonResult.status === 'match'
          }
        ]}
        large
      >
        <Modal.Section>
          {comparisonResult ? (
            <FormLayout>
              <LegacyStack distribution="center" spacing="loose">
                {renderResultStatus(comparisonResult.status)}
              </LegacyStack>
              
              <Banner status="info">
                <p>{comparisonResult.summary}</p>
              </Banner>
              
              {comparisonResult.missingMeasurements && comparisonResult.missingMeasurements.length > 0 && (
                <Banner status="warning" title="Missing Measurements">
                  <p>The product sizechart is missing the following measurements:</p>
                  <ul>
                    {comparisonResult.missingMeasurements.map((measurement, index) => (
                      <li key={`missing-${index}`}>{measurement}</li>
                    ))}
                  </ul>
                </Banner>
              )}
              
              {comparisonResult.missingSizes && comparisonResult.missingSizes.length > 0 && (
                <Banner status="warning" title="Missing Sizes">
                  <p>The product sizechart is missing the following sizes:</p>
                  <ul>
                    {comparisonResult.missingSizes.map((size, index) => (
                      <li key={`missing-size-${index}`}>{size}</li>
                    ))}
                  </ul>
                </Banner>
              )}
              
              {comparisonResult.newSizes && comparisonResult.newSizes.length > 0 && (
                <Banner status="info" title="New Sizes">
                  <p>The product sizechart includes the following new sizes not in the master template:</p>
                  <ul>
                    {comparisonResult.newSizes.map((size, index) => (
                      <li key={`new-size-${index}`}>{size}</li>
                    ))}
                  </ul>
                </Banner>
              )}
              
              {comparisonResult.deviations && comparisonResult.deviations.length > 0 && (
                <Card title="Measurement Deviations">
                  <DataTable
                    columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'text']}
                    headings={['Size', 'Measurement', 'Master Value', 'Product Value', 'Difference', 'Status']}
                    rows={getDeviationRows()}
                  />
                </Card>
              )}
            </FormLayout>
          ) : (
            <LegacyStack distribution="center" spacing="loose">
              <Spinner size="large" />
              <Text variant="headingMd">Loading comparison results...</Text>
            </LegacyStack>
          )}
        </Modal.Section>
      </Modal>
    </div>
  );
}
